import { useEffect, useState } from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const API_URL =
  process.env.REACT_APP_API_URL;


function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY
  };
}


function delay(ms) {
  return new Promise(
    resolve =>
      setTimeout(resolve, ms)
  );
}


function isDocxName(name) {
  return /\.docx$/i.test(
    String(name || '').trim()
  );
}


function pdfDownloadName(
  name
) {
  const value =
    String(
      name ||
      'assignment'
    ).trim();

  if (
    /\.pdf$/i.test(
      value
    )
  ) {
    return value;
  }

  if (
    /\.docx$/i.test(
      value
    )
  ) {
    return value.replace(
      /\.docx$/i,
      '.pdf'
    );
  }

  return value + '.pdf';
}


export default function GradingPdfViewer({
  submissionId = null,
  submissionFileId = null,
  fileName,
  reviewPdfDriveFileId,
  refreshToken = ''
}) {
  const [pdfUrl, setPdfUrl] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';


    async function ensureDocxReviewPdf() {
      if (
        submissionFileId ||
        !isDocxName(fileName) ||
        reviewPdfDriveFileId
      ) {
        return;
      }

      if (!API_URL) {
        throw new Error(
          'The assignment conversion service is not configured.'
        );
      }


      await fetch(
        API_URL,
        {
          method: 'POST',
          mode: 'no-cors',

          headers: {
            'Content-Type':
              'application/json'
          },

          body: JSON.stringify({
            action:
              'ensureAssignmentReviewPdf',

            submissionId
          })
        }
      );


      for (
        let i = 0;
        i < 20;
        i += 1
      ) {
        const response =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/assignment_submissions?id=eq.' +
              encodeURIComponent(
                submissionId
              ) +
              '&select=review_pdf_drive_file_id',
            {
              headers:
                apiHeaders()
            }
          );

        if (!response.ok) {
          throw new Error(
            'Could not check the PDF rendition.'
          );
        }

        const rows =
          await response.json();

        if (
          rows?.[0]
            ?.review_pdf_drive_file_id
        ) {
          return;
        }

        await delay(750);
      }


      throw new Error(
        'The PDF rendition could not be prepared. The original file is still available for download.'
      );
    }


    async function load() {
      setLoading(true);
      setError('');
      setPdfUrl('');

      try {
        await ensureDocxReviewPdf();

        const query =
          submissionFileId
            ? (
                'submission_file_id=' +
                encodeURIComponent(
                  submissionFileId
                )
              )
            : (
                'submission_id=' +
                encodeURIComponent(
                  submissionId
                )
              );

        const response =
          await fetch(
            SUPABASE_URL +
              '/functions/v1/peer-review-pdf?' +
              query + '&_refresh=' + Date.now(),
            {
              headers:
                apiHeaders()
            }
          );

        if (!response.ok) {
          const detail =
            await response.text();

          throw new Error(
            'Could not load the document (' +
              response.status +
              '): ' +
              detail
          );
        }


        const bytes =
          await response.arrayBuffer();

        if (cancelled) {
          return;
        }


        const blob =
          new Blob(
            [bytes],
            {
              type:
                'application/pdf'
            }
          );

        objectUrl =
          URL.createObjectURL(
            blob
          );

        setPdfUrl(
          objectUrl
        );

      } catch (err) {
        console.error(
          'Grading PDF viewer failed:',
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              'The document could not be displayed.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }


    if (
      submissionId ||
      submissionFileId
    ) {
      load();
    }


    return () => {
      cancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };

  }, [
    submissionId,
    submissionFileId,
    fileName,
    reviewPdfDriveFileId,
    refreshToken
  ]);


  if (loading) {
    return (
      <div style={styles.state}>
        Loading document...
      </div>
    );
  }


  if (error) {
    return (
      <div style={styles.error}>
        {error}
      </div>
    );
  }


  if (!pdfUrl) {
    return null;
  }


  return (
    <>
      <div
        style={styles.viewerFrame}
        title="Drag the bottom edge to resize"
      >
        <iframe
          src={pdfUrl}
          title="Student submission PDF"
          style={styles.viewer}
        />
      </div>

      <a
        href={pdfUrl}
        download={
          pdfDownloadName(
            fileName
          )
        }
        style={
          styles.downloadLink
        }
      >
        Download PDF of document
      </a>
    </>
  );
}


const styles = {
  viewerFrame: {
    width: '100%',
    height: '68vh',
    minHeight: '320px',
    maxHeight: '85vh',
    resize: 'vertical',
    overflow: 'hidden',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    background: 'white',
    boxSizing: 'border-box'
  },

  viewer: {
    display: 'block',
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'white'
  },

  downloadLink: {
  display:
    'inline-block',
  marginTop:
    '8px',
  marginRight:
    '14px',
  color:
    '#215b7d',
    fontSize:
      '13px',
    textDecoration:
      'underline'
  },

  state: {
    minHeight: '220px',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    border:
      '1px solid #d7dee5',

    borderRadius: '8px',
    background: '#f7f9fa',
    color: '#66788a',
    fontSize: '14px'
  },

  error: {
    padding: '12px',

    border:
      '1px solid #e6b8b8',

    borderRadius: '8px',
    background: '#fff5f5',
    color: '#8a1f1f',
    fontSize: '13px',
    lineHeight: 1.5
  }
};
